import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Send, Bot, User, MessageSquarePlus, AlertTriangle, Paperclip, ImageIcon } from "lucide-react";
import { RevealOnScroll, PartnerSelect } from "../shared";
import {
  SERVICE_OPTIONS,
  INITIAL_FORM,
  SANDBOX_FIXED_ADDRESS,
  generateSandboxPhone,
  extractPhoneFromFormMessage,
  buildRoofPhotoMessage,
  SANDBOX_DEMO_ROOF_PHOTO_PREVIEW,
  isRoofPhotoMessage,
} from "./sandboxScript";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const SESSION_STORAGE_KEY = "ma_sandbox_session_id";
const PHONE_STORAGE_KEY = "ma_sandbox_phone";

const inputClass =
  "w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body";

const readOnlyInputClass = `${inputClass} opacity-70 cursor-not-allowed`;

const MessageBubble = ({ role, content, imagePreview }) => {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`flex items-end gap-2 max-w-[85%] ${isUser ? "flex-row-reverse" : ""}`}>
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            isUser ? "bg-[#00D2FF]/15" : "bg-[#00D2FF]/15"
          }`}
        >
          {isUser ? <User className="w-3.5 h-3.5 text-partner-accent" /> : <Bot className="w-3.5 h-3.5 text-partner-accent" />}
        </div>
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm font-body leading-relaxed ${
            isUser
              ? "bg-[#00D2FF]/15 text-partner rounded-br-md"
              : "bg-[#11151F] text-partner rounded-bl-md border border-partner"
          }`}
        >
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Roof damage"
              className="rounded-lg mb-2 max-h-36 w-full object-cover border border-partner"
            />
          )}
          {content}
        </div>
      </div>
    </div>
  );
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 429) {
    return "Too many messages. Please wait a moment and try again.";
  }
  if (status === 503) {
    return "The demo is temporarily unavailable. Please try again later.";
  }
  if (status === 404) {
    return "Session expired. Please submit the form again to start a new chat.";
  }
  return "Something went wrong. Please try again.";
};

const SandboxSection = ({ id = "sandbox" }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [formError, setFormError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chatActive, setChatActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [chatError, setChatError] = useState("");
  const [isRestoring, setIsRestoring] = useState(
    () => typeof window !== "undefined" && Boolean(sessionStorage.getItem(SESSION_STORAGE_KEY))
  );
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const chatScrollRef = useRef(null);
  const attachMenuRef = useRef(null);

  useEffect(() => {
    const restoreSession = async () => {
      const savedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!savedSessionId) {
        setIsRestoring(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/marketing-agency/sandbox/history`, {
          params: { session_id: savedSessionId },
        });

        const { session_id, messages: historyMessages } = response.data;
        const firstUserMessage = historyMessages.find((msg) => msg.role === "user")?.content;
        const savedPhone =
          sessionStorage.getItem(PHONE_STORAGE_KEY) ||
          extractPhoneFromFormMessage(firstUserMessage);

        setSessionId(session_id);
        setChatActive(true);
        setForm((prev) => ({
          ...prev,
          phone: savedPhone,
          address: SANDBOX_FIXED_ADDRESS,
        }));
        if (savedPhone) {
          sessionStorage.setItem(PHONE_STORAGE_KEY, savedPhone);
        }
        setMessages(
          historyMessages.map((msg, index) => ({
            role: msg.role,
            content: msg.content,
            id: `${msg.role}-${index}`,
            imagePreview:
              msg.role === "user" && isRoofPhotoMessage(msg.content)
                ? SANDBOX_DEMO_ROOF_PHOTO_PREVIEW
                : undefined,
          }))
        );
      } catch (error) {
        console.error("Sandbox session restore failed:", error);
        if (error?.response?.status === 404) {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          sessionStorage.removeItem(PHONE_STORAGE_KEY);
        } else {
          setChatError(getErrorMessage(error));
        }
      } finally {
        setIsRestoring(false);
      }
    };

    restoreSession();
  }, []);

  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isSending]);

  useEffect(() => {
    if (!attachMenuOpen) return;

    const handleClickOutside = (event) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target)) {
        setAttachMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [attachMenuOpen]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" || name === "address") return;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError("");
  };

  const sendChatMessage = useCallback(
    async (text, { imagePreview } = {}) => {
      if (!text || !sessionId || isSending) return;

      const messageId = `user-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { role: "user", content: text, id: messageId, imagePreview },
      ]);
      setChatError("");
      setIsSending(true);
      setAttachMenuOpen(false);

      try {
        const response = await axios.post(`${BACKEND_URL}/api/marketing-agency/sandbox/message`, {
          session_id: sessionId,
          message: text,
        });

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.data.response_text, id: `bot-${Date.now()}` },
        ]);
      } catch (error) {
        console.error("Sandbox message failed:", error);
        setChatError(getErrorMessage(error));
      } finally {
        setIsSending(false);
      }
    },
    [sessionId, isSending]
  );

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.fullName || !form.email) {
      setFormError("Please fill in all required fields.");
      return;
    }

    const phone = generateSandboxPhone();

    setFormError("");
    setChatError("");
    setIsSending(true);
    setChatActive(true);
    setMessages([]);
    setSessionId(null);
    setAttachMenuOpen(false);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(PHONE_STORAGE_KEY);
    setForm((prev) => ({
      ...prev,
      phone,
      address: SANDBOX_FIXED_ADDRESS,
    }));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/marketing-agency/sandbox/start`, {
        service: form.service,
        fullName: form.fullName,
        phone,
        email: form.email,
        address: SANDBOX_FIXED_ADDRESS,
      });

      const { session_id, user_message, response_text } = response.data;
      setSessionId(session_id);
      sessionStorage.setItem(SESSION_STORAGE_KEY, session_id);
      sessionStorage.setItem(PHONE_STORAGE_KEY, phone);
      setMessages([
        { role: "user", content: user_message, id: "user-0" },
        { role: "assistant", content: response_text, id: "bot-0" },
      ]);
    } catch (error) {
      console.error("Sandbox start failed:", error);
      setChatActive(false);
      setChatError(getErrorMessage(error));
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = userInput.trim();
    if (!text || !chatActive || !sessionId || isSending) return;

    setUserInput("");
    await sendChatMessage(text);
  };

  const handleSendRoofPhoto = async () => {
    if (!chatActive || !sessionId || isSending || isRestoring) return;
    await sendChatMessage(buildRoofPhotoMessage(), {
      imagePreview: SANDBOX_DEMO_ROOF_PHOTO_PREVIEW,
    });
  };

  const handleNewChat = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(PHONE_STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setUserInput("");
    setChatError("");
    setChatActive(false);
    setAttachMenuOpen(false);
    setForm({ ...INITIAL_FORM });
    setShowNewChatConfirm(false);
  };

  const canUseAttachments = chatActive && sessionId && !isSending && !isRestoring;

  return (
    <section id={id} className="section-padding section-alt relative overflow-hidden" data-testid="sandbox-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <RevealOnScroll className="mb-10">
          <span className="badge-mono text-partner-accent bg-[#00D2FF]/10 border border-[#00D2FF]/20 px-3 py-1.5 rounded-full inline-block mb-6">
            LIVE INFRASTRUCTURE SANDBOX
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 leading-tight">
            Test Roofing AI Employee in <span className="gradient-text">Real-Time</span>
          </h2>
          <p className="text-partner-muted font-body leading-relaxed max-w-3xl mb-2">
            Fill out the form to activate a live roofing AI employee for emergency vetting, lead qualification, and{" "}
            <strong className="text-partner">Roofing Inspection</strong> booking in real time.
          </p>
          <p className="text-sm font-mono text-partner-accent max-w-3xl" data-testid="sandbox-vertical-note">
            Roofing example. Every niche gets its own configuration.
          </p>
        </RevealOnScroll>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <RevealOnScroll delay={0.1}>
            <div className="bento-card p-6 sm:p-8 border border-partner h-full">
              <h3 className="text-lg font-bold text-partner mb-6 flex items-center gap-2">
                <span className="badge-mono text-[10px] text-partner-accent bg-[#00D2FF]/10 px-2 py-1 rounded">STEP 1</span>
                Lead Submits a Form
              </h3>
              <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="sandbox-form">
                <div>
                  <label className="block text-xs text-partner-muted mb-1.5 font-body">
                    What service do you require? *
                  </label>
                  <PartnerSelect
                    name="service"
                    value={form.service}
                    onChange={handleFormChange}
                    required
                    placeholder="Select a service"
                    options={SERVICE_OPTIONS}
                  />
                </div>
                <div>
                  <label className="block text-xs text-partner-muted mb-1.5 font-body">Full Name</label>
                  <input name="fullName" value={form.fullName} onChange={handleFormChange} className={inputClass} placeholder="John Smith" required />
                </div>
                <div>
                  <label className="block text-xs text-partner-muted mb-1.5 font-body">Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    readOnly
                    disabled
                    placeholder="Assigned when you start the chat"
                    className={readOnlyInputClass}
                    data-testid="sandbox-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs text-partner-muted mb-1.5 font-body">Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleFormChange} className={inputClass} placeholder="john@example.com" required />
                </div>
                <div>
                  <label className="block text-xs text-partner-muted mb-1.5 font-body">Address</label>
                  <input
                    name="address"
                    value={SANDBOX_FIXED_ADDRESS}
                    readOnly
                    disabled
                    className={readOnlyInputClass}
                    data-testid="sandbox-address"
                  />
                </div>
                {formError && <p className="text-red-400 text-xs">{formError}</p>}
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full bg-gradient-to-r from-[#00D2FF] to-[#00E5FF] text-partner font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="sandbox-submit"
                >
                  {isSending && !sessionId ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Activating AI Employee…
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </form>
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <div className="bento-card border border-[#00D2FF]/20 flex flex-col h-[520px] sm:h-[560px]">
              <div className="px-5 py-4 border-b border-partner flex items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-partner flex items-center gap-2 min-w-0">
                  <span className="badge-mono text-[10px] text-partner-accent bg-[#00D2FF]/10 px-2 py-1 rounded shrink-0">STEP 2</span>
                  <span className="truncate">FractoLabs Core Activation</span>
                </h3>
                <div className="flex items-center gap-2 shrink-0">
                  {chatActive && sessionId && !isRestoring && (
                    <button
                      type="button"
                      onClick={() => setShowNewChatConfirm(true)}
                      disabled={isSending}
                      className="flex items-center gap-1.5 text-xs font-mono text-partner-muted hover:text-partner-accent border border-partner hover:border-[#00D2FF]/30 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-40"
                      data-testid="sandbox-new-chat"
                    >
                      <MessageSquarePlus className="w-3.5 h-3.5" />
                      New chat
                    </button>
                  )}
                  {chatActive && (
                    <span className="flex items-center gap-1.5 text-xs font-mono text-partner-accent">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00D2FF] pulse-subtle" />
                      ACTIVE
                    </span>
                  )}
                </div>
              </div>

              <div
                ref={chatScrollRef}
                className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"
                data-testid="sandbox-chat"
              >
                {!isRestoring && !chatActive ? (
                  <div className="h-full flex items-center justify-center text-center px-6">
                    <p className="text-partner-muted text-sm font-body">
                      {chatError || "Submit the form to activate the AI Employee. Responses appear here instantly."}
                    </p>
                  </div>
                ) : isRestoring ? (
                  <div className="h-full flex items-center justify-center text-center px-6">
                    <div className="flex items-center gap-2 text-partner-muted text-sm font-body">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Restoring your session…
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <MessageBubble
                        key={msg.id}
                        role={msg.role}
                        content={msg.content}
                        imagePreview={msg.imagePreview}
                      />
                    ))}
                    {isSending && (
                      <div className="flex items-center gap-2 text-partner-muted text-xs font-mono pl-9">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        AI responding…
                      </div>
                    )}
                    {chatError && (
                      <p className="text-red-400 text-xs font-body px-2 mt-2">{chatError}</p>
                    )}
                  </>
                )}
              </div>

              <form onSubmit={handleSendMessage} className="px-4 py-3 border-t border-partner flex gap-2 items-center">
                <div className="relative shrink-0" ref={attachMenuRef}>
                  <button
                    type="button"
                    onClick={() => setAttachMenuOpen((open) => !open)}
                    disabled={!canUseAttachments}
                    className="p-2.5 rounded-lg border border-partner text-partner-muted hover:text-partner-accent hover:border-[#00D2FF]/30 hover:bg-[#00D2FF]/10 disabled:opacity-40 transition-colors"
                    aria-label="Attach"
                    aria-expanded={attachMenuOpen}
                    data-testid="sandbox-attach"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  {attachMenuOpen && canUseAttachments && (
                    <div
                      className="absolute bottom-full left-0 mb-2 w-52 rounded-xl border border-partner bg-[#11151F] shadow-xl overflow-hidden z-10"
                      data-testid="sandbox-attach-menu"
                    >
                      <button
                        type="button"
                        onClick={handleSendRoofPhoto}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm font-body text-partner hover:bg-[#00D2FF]/10 transition-colors"
                        data-testid="sandbox-send-roof-photo"
                      >
                        <ImageIcon className="w-4 h-4 text-partner-accent shrink-0" />
                        <span>Send roof photo</span>
                      </button>
                      <p className="px-3 pb-2.5 text-[10px] text-partner-muted font-body leading-snug">
                        Use when the AI asks for roof photos.
                      </p>
                    </div>
                  )}
                </div>
                <input
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  disabled={!chatActive || isSending || !sessionId || isRestoring}
                  placeholder={chatActive ? "Ask about pricing, booking, or next steps…" : "Activate chat first"}
                  className={`${inputClass} flex-1 disabled:opacity-40`}
                />
                <button
                  type="submit"
                  disabled={!chatActive || !userInput.trim() || isSending || !sessionId || isRestoring}
                  className="p-2.5 rounded-lg bg-[#00D2FF]/15 border border-[#00D2FF]/30 text-partner-accent hover:bg-[#00D2FF]/25 disabled:opacity-40 transition-colors shrink-0"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </RevealOnScroll>
        </div>
      </div>

      {showNewChatConfirm && (
        <div
          className="fixed inset-0 bg-[#080A0F]/70 backdrop-blur-sm flex items-center justify-center z-[100] px-4"
          data-testid="sandbox-new-chat-confirm"
        >
          <div className="bento-card border border-[#00D2FF]/20 p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#00D2FF]/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-partner-accent" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-partner mb-1">Start a new chat?</h3>
                <p className="text-sm text-partner-muted font-body">
                  Your current conversation will be cleared from this browser. You&apos;ll need to submit the form again to start fresh.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNewChatConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-partner text-partner text-sm font-semibold hover:bg-[#11151F] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNewChat}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#00D2FF] to-[#00E5FF] text-partner text-sm font-semibold hover:opacity-90 transition-opacity"
                data-testid="sandbox-new-chat-confirm-btn"
              >
                Start new chat
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SandboxSection;
